import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import AXIOS_INSTANCE from '../api/axiosInstance'
import {
  catalogResultRefsFromIncludedOrders,
  imagingResultsFromBundleIncluded,
  toIncludedImagingResult,
  withResultOrderRelationship
} from '../pages/Doctor/Encounter/Manage Orders/Modals/ImagingDetails.helpers'
import type {
  ImagingDataMock,
  ImagingOrderIncludedImagingResult,
  ImagingOrderIncludedResource
} from '../types/imaging_order.type'
import type { ImagingResultResponse } from '../types/imaging_results.type'
import type { OrderDataMock, OrderIncluded } from '../types/order.type'

type OrderDetailPayload = {
  data: OrderDataMock | OrderDataMock[]
  included?: OrderIncluded[]
}

function orderFromPayload(payload: OrderDetailPayload | undefined): OrderDataMock | undefined {
  if (!payload?.data) return undefined
  return Array.isArray(payload.data) ? payload.data[0] : payload.data
}

function imagingResultIdFromOrder(order: OrderDataMock | undefined): string | undefined {
  const rel = order?.relationships?.imaging_result
  if (rel && typeof rel === 'object' && 'data' in rel && rel.data?.id != null) {
    return String(rel.data.id)
  }
  return undefined
}

function collectIncludedImagingResults(
  payload: OrderDetailPayload | undefined,
  orderId: string
): ImagingOrderIncludedImagingResult[] {
  if (!payload?.included?.length) return []
  return payload.included
    .filter((item): item is OrderIncluded & { type: 'imaging_result' } => item.type === 'imaging_result')
    .map(row =>
      withResultOrderRelationship(
        toIncludedImagingResult({
          id: row.id,
          type: 'imaging_result',
          attributes: row.attributes as ImagingOrderIncludedImagingResult['attributes'],
          relationships: row.relationships as ImagingOrderIncludedImagingResult['relationships']
        }),
        orderId
      )
    )
}

/**
 * Parent imaging_order included results plus catalog child-order results.
 * Cold load must work without React Query seed: parent child refs, order show
 * include, and relationship linkage → GET /imaging_results/:id.
 */
export function useBundleImagingResults(
  order: ImagingDataMock | undefined,
  included: (ImagingOrderIncludedResource | OrderIncluded)[] | undefined
) {
  const fromParent = useMemo(() => imagingResultsFromBundleIncluded(order, included), [included, order])

  const parentCatalogRefs = useMemo(() => catalogResultRefsFromIncludedOrders(order, included), [included, order])

  const catalogOrderIds = useMemo(() => {
    if (!order || order.type !== 'imaging_order') return [] as string[]
    return (order.relationships.orders?.data ?? []).map(ref => ref.id)
  }, [order])

  const orderQueries = useQueries({
    queries: catalogOrderIds.map(orderId => ({
      enabled: !!orderId,
      queryFn: async () => {
        const { data } = await AXIOS_INSTANCE.get<OrderDetailPayload>(`/orders/${orderId}`)
        return data
      },
      queryKey: ['orders', { id: orderId }] as const
    }))
  })

  const catalogFromOrders: ImagingOrderIncludedImagingResult[] = []
  const resultIdsToFetch: { orderId: string; resultId: string }[] = []
  const seenResult = new Set<string>(fromParent.map(r => String(r.id)))
  const queuedResultIds = new Set<string>()

  const queueResultFetch = (orderId: string, resultId: string) => {
    if (!resultId || seenResult.has(resultId) || queuedResultIds.has(resultId)) return
    queuedResultIds.add(resultId)
    resultIdsToFetch.push({ orderId, resultId })
  }

  // Parent imaging_order nested child orders may already expose imaging_result linkage.
  for (const ref of parentCatalogRefs) {
    queueResultFetch(ref.orderId, ref.resultId)
  }

  catalogOrderIds.forEach((orderId, index) => {
    const payload = orderQueries[index]?.data as OrderDetailPayload | undefined
    if (!payload) return

    const includedResults = collectIncludedImagingResults(payload, orderId)
    for (const row of includedResults) {
      if (seenResult.has(row.id)) continue
      seenResult.add(row.id)
      catalogFromOrders.push(row)
    }

    // Always follow relationship linkage when included document is missing
    // (older backends / sparse includes). Do not treat empty included as "no result".
    const childOrder = orderFromPayload(payload)
    const resultId = imagingResultIdFromOrder(childOrder)
    if (resultId && !seenResult.has(resultId)) {
      queueResultFetch(orderId, resultId)
    }
  })

  const resultQueries = useQueries({
    queries: resultIdsToFetch.map(({ resultId }) => ({
      enabled: !!resultId,
      queryFn: async () => {
        const { data } = await AXIOS_INSTANCE.get<ImagingResultResponse>(`/imaging_results/${resultId}`)
        return data
      },
      queryKey: ['imaging_result', { resultId }] as const
    }))
  })

  const byId = new Map<string, ImagingOrderIncludedImagingResult>()
  for (const result of fromParent) byId.set(String(result.id), result)
  for (const result of catalogFromOrders) byId.set(String(result.id), result)

  resultIdsToFetch.forEach((entry, index) => {
    const row = resultQueries[index]?.data?.data
    if (!row || row.type !== 'imaging_result') return
    byId.set(
      String(row.id),
      withResultOrderRelationship(
        toIncludedImagingResult({
          id: row.id,
          type: 'imaging_result',
          attributes: row.attributes as ImagingOrderIncludedImagingResult['attributes'],
          relationships: row.relationships as ImagingOrderIncludedImagingResult['relationships']
        }),
        entry.orderId
      )
    )
  })

  const imagingResults = Array.from(byId.values())
  const isLoading = orderQueries.some(q => q.isPending) || resultQueries.some(q => q.isPending)

  return { imagingResults, isLoading }
}
